import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import Button from '../../../components/general/Button';
import Loader from '../../../components/general/Loader';
import PageError from '../../../components/general/PageError';
import StyledDropdown from '../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import { color_defaults_white, color_shades_dark, color_highlights_blue } from '../../../constants/colors';
import { aiAnsweringCommonAppName, aiDentalSchedulingCommonAppName } from '../../../constants/sparkConstants/moduleConsts';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import MedicalIntegrationConfigModel from '../../../models/organizations/MedicalIntegrationConfigModel';
import MedicalIntegrationConfigContainer from '../../../hooks/MedicalIntegrationConfigContainer';
import useBulkAppOperations from '../../../hooks/useBulkAppOperations';
import * as XLSX from 'xlsx';

// Practice Management System options
const PRACTICE_MANAGEMENT_SYSTEMS = ['Denticon', 'Dentrix Ascend', 'Cloud 9', 'OpenDental', 'Custom'] as const;
type PracticeManagementSystem = typeof PRACTICE_MANAGEMENT_SYSTEMS[number];

interface OrganizationHealthcareManagementSettingsProps {
    organizationId: string;
    canManageQueues?: boolean;
    applicationContainer: ApplicationContainer;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
}

const OrganizationHealthcareManagementSettingsInner: React.FC<OrganizationHealthcareManagementSettingsProps> = ({
    organizationId,
    canManageQueues = false,
    applicationContainer,
    appliedTemplateContainer,
    applicationEnvironmentsContainer
}) => {
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
    const [sharedTopicsApp, setSharedTopicsApp] = useState<ApplicationModel | null>(null);
    const [locationSpecificFields, setLocationSpecificFields] = useState<TemplateFormFieldModel[]>([]);
    const [isLoadingSharedTopics, setIsLoadingSharedTopics] = useState(false);
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<string>('');
    const [importedData, setImportedData] = useState<any[] | null>(null);
    const [isApplying, setIsApplying] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Bulk app operations hook
    const { isProcessing: isPublishing, logs: bulkOperationLogs, syncApps, publishApps, clearLogs } = useBulkAppOperations({
        organizationId,
        appliedTemplateContainer,
        applicationEnvironmentsContainer
    });

    // Medical Integration Configuration - use container
    const medicalConfigContainer = MedicalIntegrationConfigContainer.useContainer();
    const [selectedMedicalConfig, setSelectedMedicalConfig] = useState<MedicalIntegrationConfigModel | null>(null);
    const [isCreatingNewConfig, setIsCreatingNewConfig] = useState(false);
    const [copiedConfigId, setCopiedConfigId] = useState<string | null>(null);
    const [isCustomPMS, setIsCustomPMS] = useState(false);
    const [medicalConfigForm, setMedicalConfigForm] = useState<MedicalIntegrationConfigModel>({
        organizationId: organizationId,
        clientName: '',
        configuration: '{}',
        integrationType: ''
    });

    // Helper to check if a value is a known PMS option (excluding 'Custom')
    const isKnownPMS = (value: string): boolean => {
        return PRACTICE_MANAGEMENT_SYSTEMS.slice(0, -1).includes(value as PracticeManagementSystem);
    };

    useEffect(() => {
        // Apps are already loaded by parent component (OrganizationSettingsTabs)
        if (appliedTemplateContainer) {
            loadSharedTopicsFields();
        }
    }, [organizationId]);

    // Medical Integration Configuration functions
    const loadMedicalConfigs = async () => {
        setSuccessMessage(null);
        setErrors([]);
        const result = await medicalConfigContainer.loadConfigsByOrganizationId(organizationId);
        if (result.resultType === "Ok") {
            setSuccessMessage(`Successfully loaded ${result.data?.length || 0} medical integration configuration(s)`);
        } else {
            setErrors(['Failed to load medical integration configurations: ' + (result.errors?.join(', ') || 'Unknown error')]);
        }
    };

    const selectMedicalConfig = (config: MedicalIntegrationConfigModel) => {
        setSuccessMessage(null);
        setErrors([]);
        setSelectedMedicalConfig(config);
        medicalConfigContainer.selectConfig(config);
        setIsCreatingNewConfig(false);
        setIsCustomPMS(!isKnownPMS(config.integrationType));
        setMedicalConfigForm({
            id: config.id,
            organizationId: config.organizationId,
            clientName: config.clientName,
            configuration: config.configuration,
            integrationType: config.integrationType
        });
    };

    const startNewMedicalConfig = () => {
        setSuccessMessage(null);
        setErrors([]);
        setSelectedMedicalConfig(null);
        medicalConfigContainer.selectConfig(null);
        setIsCreatingNewConfig(true);
        setIsCustomPMS(false);
        setMedicalConfigForm({
            organizationId: organizationId,
            clientName: '',
            configuration: '{}',
            integrationType: ''
        });
    };

    const cancelMedicalConfigEdit = () => {
        setSuccessMessage(null);
        setErrors([]);
        setSelectedMedicalConfig(null);
        medicalConfigContainer.selectConfig(null);
        setIsCreatingNewConfig(false);
        setIsCustomPMS(false);
        setMedicalConfigForm({
            organizationId: organizationId,
            clientName: '',
            configuration: '{}',
            integrationType: ''
        });
    };

    const saveMedicalConfig = async () => {
        if (!medicalConfigForm.clientName.trim()) {
            setSuccessMessage(null);
            setErrors(['Client Name is required']);
            return;
        }
        if (!medicalConfigForm.integrationType.trim()) {
            setSuccessMessage(null);
            setErrors(['Integration Type is required']);
            return;
        }

        // Validate JSON
        try {
            JSON.parse(medicalConfigForm.configuration);
        } catch (e) {
            setSuccessMessage(null);
            setErrors(['Configuration must be valid JSON']);
            return;
        }

        setErrors([]);
        setSuccessMessage(null);

        let result;
        if (isCreatingNewConfig) {
            result = await medicalConfigContainer.createConfig(medicalConfigForm);
        } else if (selectedMedicalConfig?.id) {
            result = await medicalConfigContainer.updateConfig(selectedMedicalConfig.id, medicalConfigForm);
        } else {
            setErrors(['No configuration selected for update']);
            return;
        }

        if (result.resultType === "Ok") {
            setSuccessMessage(`Successfully ${isCreatingNewConfig ? 'created' : 'updated'} medical integration configuration`);
            // Reload configs after create to ensure we have the new config with its ID
            if (isCreatingNewConfig) {
                await medicalConfigContainer.loadConfigsByOrganizationId(organizationId);
            }
            cancelMedicalConfigEdit();
        } else {
            setErrors([`Failed to ${isCreatingNewConfig ? 'create' : 'update'} configuration: ` + (result.errors?.join(', ') || 'Unknown error')]);
        }
    };

    const formatJson = () => {
        try {
            const parsed = JSON.parse(medicalConfigForm.configuration);
            setMedicalConfigForm({
                ...medicalConfigForm,
                configuration: JSON.stringify(parsed, null, 2)
            });
        } catch (e) {
            setErrors(['Cannot format: Configuration is not valid JSON']);
        }
    };

    // Filter applications for healthcare organizations
    const healthcareApps = applicationContainer.state.baseOrgApplications.filter(app => {
        // Filter out any apps that might be system apps
        if (app?.name?.toLowerCase()?.includes("manage menu") ||
            app?.name?.toLowerCase()?.includes("manage entities") ||
            (app?.name?.toLowerCase()?.startsWith("x -") && app?.name?.toLowerCase()?.endsWith("menu"))) {
            return false;
        }
        return true;
    });

    // Filter apps based on search term
    const filteredApps = healthcareApps.filter(app => {
        if (!searchTerm.trim()) return true;

        const appName = app.name?.toLowerCase() || '';
        const terms = searchTerm.split('|').map(t => t.trim()).filter(t => t);

        const exclusionTerms: string[] = [];
        const inclusionTerms: string[] = [];

        terms.forEach(term => {
            const exclusionMatch = term.match(/^!"(.+)"$/) || term.match(/^!'(.+)'$/);
            if (exclusionMatch) {
                exclusionTerms.push(exclusionMatch[1].toLowerCase());
            } else {
                const cleanTerm = term.replace(/^["']|["']$/g, '');
                inclusionTerms.push(cleanTerm.toLowerCase());
            }
        });

        for (const excludeTerm of exclusionTerms) {
            if (appName.includes(excludeTerm)) {
                return false;
            }
        }

        if (inclusionTerms.length === 0) {
            return true;
        }

        return inclusionTerms.every(term => appName.includes(term));
    });

    const loadSharedTopicsFields = async () => {
        setIsLoadingSharedTopics(true);
        try {
            const appsResult = await applicationContainer.searchForOrgApplications(organizationId, 1, 50, undefined, undefined, true);
            const apps = appsResult?.data?.applications ?? [];

            let sharedTopics = apps.find(app =>
                app.name?.toLowerCase()?.trim() === aiDentalSchedulingCommonAppName.toLowerCase() && app.allowsTemplating
            );

            if (!sharedTopics) {
                sharedTopics = apps.find(app =>
                    app.name?.toLowerCase()?.includes('shared topics') && app.allowsTemplating
                );
            }

            if (!sharedTopics) {
                sharedTopics = apps.find(app =>
                    app.name?.toLowerCase()?.includes('shared topics')
                );
            }

            if (sharedTopics && appliedTemplateContainer) {
                setSharedTopicsApp(sharedTopics);

                const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(sharedTopics.id);

                if (appliedTemplatesResult?.resultType === "Ok") {
                    const appliedTemplates = appliedTemplatesResult.data;

                    const locationFields: TemplateFormFieldModel[] = [];
                    const fieldSet = new Set<string>();

                    appliedTemplates.forEach(appliedTemplate => {
                        appliedTemplate.templateForm?.templateFormSections?.forEach(section => {
                            section.templateFormFields?.forEach(field => {
                                const fieldValue = appliedTemplate.templateFormFieldValues?.find(
                                    v => v.templateFormFieldId === field.id
                                );

                                if (fieldValue?.associatedVariable && fieldValue.associatedVariable === fieldValue.value) {
                                    if (!fieldSet.has(field.title)) {
                                        fieldSet.add(field.title);
                                        locationFields.push({
                                            ...field,
                                            name: section.title
                                        });
                                    }
                                }
                            });
                        });
                    });

                    setLocationSpecificFields(locationFields);
                }
            }
        } catch (error) {
            console.error('Error loading shared topics fields:', error);
        } finally {
            setIsLoadingSharedTopics(false);
        }
    };

    const toggleAppSelection = (appId: string) => {
        const newSelected = new Set(selectedApps);
        if (newSelected.has(appId)) {
            newSelected.delete(appId);
        } else {
            newSelected.add(appId);
        }
        setSelectedApps(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedApps.size === healthcareApps.length) {
            setSelectedApps(new Set());
        } else {
            setSelectedApps(new Set(healthcareApps.map(app => app.id)));
        }
    };

    const selectVisibleApps = () => {
        setSelectedApps(new Set(filteredApps.map(app => app.id)));
    };

    const toggleFieldSelection = (fieldTitle: string) => {
        const newSelected = new Set(selectedFields);
        if (newSelected.has(fieldTitle)) {
            newSelected.delete(fieldTitle);
        } else {
            newSelected.add(fieldTitle);
        }
        setSelectedFields(newSelected);
    };

    const toggleAllFields = () => {
        if (selectedFields.size === locationSpecificFields.length) {
            setSelectedFields(new Set());
        } else {
            setSelectedFields(new Set(locationSpecificFields.map(field => field.title)));
        }
    };

    const publishTemplatesForSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        const selectedApplications = healthcareApps.filter(app => selectedApps.has(app.id));
        await publishApps(selectedApplications);
    };

    const moduleSyncSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        const selectedApplications = healthcareApps.filter(app => selectedApps.has(app.id));
        await syncApps(selectedApplications, sharedTopicsApp);
    };

    const importFromExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setIsImporting(true);
            setErrors([]);
            setImportProgress('Reading Excel file...');

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length < 2) {
                setErrors(['Excel file is empty or has no data rows']);
                return;
            }

            const headers = jsonData[0] as string[];
            const dataRows = jsonData.slice(1);

            const locationIdIndex = headers.findIndex(h => h === 'Location ID');
            const locationNameIndex = headers.findIndex(h => h === 'Location Name');

            if (locationIdIndex === -1 || locationNameIndex === -1) {
                setErrors(['Excel file must have "Location ID" and "Location Name" columns']);
                return;
            }

            const fieldHeaders = headers.slice(2);

            setImportProgress(`Validating ${dataRows.length} locations...`);

            const validationErrors: string[] = [];
            const parsedData: any[] = [];

            for (let i = 0; i < dataRows.length; i++) {
                const row = dataRows[i];
                const locationId = row[locationIdIndex];
                const locationName = row[locationNameIndex];

                if (!locationId) continue;

                setImportProgress(`Validating ${i + 1}/${dataRows.length}: ${locationName || locationId}...`);

                let app = applicationContainer.state.baseOrgApplications.find(
                    a => a.id === locationId
                );

                if (!app) {
                    validationErrors.push(`Location not found: ${locationName || locationId}`);
                    continue;
                }

                const fieldChanges: { fieldTitle: string; newValue: string }[] = [];
                for (let fieldIndex = 0; fieldIndex < fieldHeaders.length; fieldIndex++) {
                    const fieldTitle = fieldHeaders[fieldIndex];
                    const newValue = row[2 + fieldIndex];

                    if (newValue !== undefined && newValue !== null && newValue !== '') {
                        fieldChanges.push({
                            fieldTitle,
                            newValue: String(newValue)
                        });
                    }
                }

                if (fieldChanges.length > 0) {
                    parsedData.push({
                        locationId,
                        locationName: locationName || app.name,
                        appId: app.id,
                        fieldChanges
                    });
                }
            }

            setImportProgress('');

            if (parsedData.length === 0) {
                setErrors(['No valid data found in Excel file']);
                return;
            }

            setImportedData(parsedData);

            if (validationErrors.length > 0) {
                setErrors([
                    `Loaded ${parsedData.length} locations from Excel with ${validationErrors.length} warnings. Click "Apply Changes" to save the updates.`,
                    ...validationErrors.slice(0, 10),
                    ...(validationErrors.length > 10 ? [`...and ${validationErrors.length - 10} more warnings`] : [])
                ]);
            } else {
                setErrors([`Successfully loaded ${parsedData.length} locations from Excel. Review the changes below and click "Apply Changes" to save.`]);
            }

        } catch (error) {
            console.error('Error importing Excel:', error);
            setErrors(['Error importing Excel: ' + error.toString()]);
        } finally {
            setIsImporting(false);
            setImportProgress('');
            event.target.value = '';
        }
    };

    const applyChanges = async () => {
        if (!importedData || importedData.length === 0) {
            setErrors(['No imported data to apply']);
            return;
        }

        try {
            setIsApplying(true);
            setErrors([]);
            setImportProgress('Applying changes...');

            let successCount = 0;
            let errorCount = 0;
            const applyErrors: string[] = [];

            for (let i = 0; i < importedData.length; i++) {
                const locationData = importedData[i];

                setImportProgress(`Applying ${i + 1}/${importedData.length}: ${locationData.locationName}...`);

                try {
                    const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(locationData.appId);

                    if (appliedTemplatesResult?.resultType !== "Ok") {
                        applyErrors.push(`Failed to load templates for ${locationData.locationName}`);
                        errorCount++;
                        continue;
                    }

                    const appliedTemplates = appliedTemplatesResult.data;

                    for (const fieldChange of locationData.fieldChanges) {
                        let updated = false;
                        for (const appliedTemplate of appliedTemplates) {
                            for (const section of appliedTemplate.templateForm?.templateFormSections || []) {
                                const field = section.templateFormFields?.find(f => f.title === fieldChange.fieldTitle);

                                if (field) {
                                    let fieldValue = appliedTemplate.templateFormFieldValues?.find(
                                        v => v.templateFormFieldId === field.id
                                    );

                                    if (!fieldValue) {
                                        fieldValue = {
                                            templateFormFieldId: field.id,
                                            value: fieldChange.newValue,
                                            associatedVariable: field.associatedVariable,
                                            label: field.label,
                                            fieldType: field.fieldType,
                                            placeholder: field.placeholder,
                                            defaultValue: field.defaultValue,
                                            tip: field.tip,
                                            title: field.title,
                                            appliedApplicationTemplateId: appliedTemplate.id
                                        };
                                        appliedTemplate.templateFormFieldValues.push(fieldValue);
                                    } else {
                                        fieldValue.value = fieldChange.newValue;
                                    }

                                    updated = true;
                                    break;
                                }
                            }
                            if (updated) break;
                        }
                    }

                    let allSuccess = true;
                    for (const appliedTemplate of appliedTemplates) {
                        const updateRequest = {
                            templateFormFieldValues: appliedTemplate.templateFormFieldValues,
                            dynamicFormSections: appliedTemplate.templateForm.templateFormSections.filter(s => s?.name)
                        };

                        const result = await appliedTemplateContainer.updateAppliedTemplate(
                            appliedTemplate.id,
                            updateRequest
                        );

                        if (result?.resultType !== "Ok") {
                            allSuccess = false;
                            break;
                        }
                    }

                    if (allSuccess) {
                        successCount++;
                    } else {
                        applyErrors.push(`Failed to save updates for ${locationData.locationName}`);
                        errorCount++;
                    }

                } catch (error) {
                    console.error(`Error applying changes for location ${locationData.locationName}:`, error);
                    applyErrors.push(`Error applying changes to ${locationData.locationName}: ${error.toString()}`);
                    errorCount++;
                }
            }

            setImportProgress('');

            if (applyErrors.length > 0) {
                setErrors([
                    `Applied changes with ${successCount} successes and ${errorCount} errors:`,
                    ...applyErrors.slice(0, 10),
                    ...(applyErrors.length > 10 ? [`...and ${applyErrors.length - 10} more errors`] : [])
                ]);
            } else {
                setErrors([`Successfully applied changes to ${successCount} locations!`]);
                setImportedData(null);
            }

        } catch (error) {
            console.error('Error applying changes:', error);
            setErrors(['Error applying changes: ' + error.toString()]);
        } finally {
            setIsApplying(false);
            setImportProgress('');
        }
    };

    const exportToExcel = async () => {
        if (selectedApps.size === 0 || selectedFields.size === 0) {
            setErrors(['Please select at least one application/location and one field to export']);
            return;
        }

        try {
            setIsLoadingSharedTopics(true);

            const selectedApplications = healthcareApps.filter(app => selectedApps.has(app.id));
            const selectedFieldsList = locationSpecificFields.filter(field => selectedFields.has(field.title));

            const headers = ['Location ID', 'Location Name', ...selectedFieldsList.map(field => field.title)];

            const data: any[][] = [];

            for (const app of selectedApplications) {
                const row: any[] = [
                    app.id,
                    app.name
                ];

                if (appliedTemplateContainer) {
                    const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(app.id);

                    if (appliedTemplatesResult?.resultType === "Ok") {
                        const appliedTemplates = appliedTemplatesResult.data;

                        for (const selectedField of selectedFieldsList) {
                            let fieldValue = '';

                            for (const template of appliedTemplates) {
                                const field = template.templateForm?.templateFormSections
                                    ?.flatMap(section => section.templateFormFields ?? [])
                                    ?.find(f => f.title === selectedField.title);

                                if (field) {
                                    const value = template.templateFormFieldValues?.find(
                                        v => v.templateFormFieldId === field.id
                                    );

                                    if (value?.value) {
                                        fieldValue = value.value;
                                        break;
                                    }
                                }
                            }

                            row.push(fieldValue);
                        }
                    } else {
                        selectedFieldsList.forEach(() => row.push(''));
                    }
                } else {
                    selectedFieldsList.forEach(() => row.push(''));
                }

                data.push(row);
            }

            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Location Specific Topics');

            const timestamp = new Date().toISOString().split('T')[0];
            const fileName = `healthcare_location_topics_${timestamp}.xlsx`;

            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setErrors(['Error exporting to Excel: ' + error.toString()]);
        } finally {
            setIsLoadingSharedTopics(false);
        }
    };

    // Parent component (OrganizationSettingsTabs) handles loading state
    // No need to show loader here

    return (
        <Container>
            {/* Medical Integration Configuration Section */}
            <Header>
                <Title>Medical Integration Configuration</Title>
                <Description>
                    Manage medical integration configurations for this organization. Load existing configurations, create new ones, or update existing ones.
                </Description>
            </Header>

            <MedicalConfigSection>
                <MedicalConfigActions>
                    <Button
                        themes={['secondary']}
                        text={medicalConfigContainer.isLoading ? 'Loading...' : 'Load Configurations'}
                        onClick={loadMedicalConfigs}
                        disabled={medicalConfigContainer.isLoading}
                    />
                    <Button
                        themes={['primary']}
                        text="Create New Configuration"
                        onClick={startNewMedicalConfig}
                        disabled={medicalConfigContainer.isLoading || medicalConfigContainer.isSaving}
                    />
                </MedicalConfigActions>

                {medicalConfigContainer.medicalConfigs.length > 0 && (
                    <MedicalConfigList>
                        <MedicalConfigListHeader>Existing Configurations ({medicalConfigContainer.medicalConfigs.length})</MedicalConfigListHeader>
                        {medicalConfigContainer.medicalConfigs.filter(config => config && config.id).map((config) => (
                            <MedicalConfigCard
                                key={config.id}
                                selected={selectedMedicalConfig?.id === config.id}
                                onClick={() => selectMedicalConfig(config)}
                            >
                                <MedicalConfigCardContent>
                                    <MedicalConfigName>{config.clientName}</MedicalConfigName>
                                    <MedicalConfigType>PMS: {config.integrationType}</MedicalConfigType>
                                    <MedicalConfigIdRow>
                                        <MedicalConfigId>ID: {config.id}</MedicalConfigId>
                                        <CopyButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(config.id || '');
                                                setCopiedConfigId(config.id || null);
                                                setTimeout(() => setCopiedConfigId(null), 2000);
                                            }}
                                            title="Copy Configuration ID"
                                        >
                                            {copiedConfigId === config.id ? 'Copied!' : 'Copy ID'}
                                        </CopyButton>
                                    </MedicalConfigIdRow>
                                </MedicalConfigCardContent>
                            </MedicalConfigCard>
                        ))}
                    </MedicalConfigList>
                )}

                {(isCreatingNewConfig || selectedMedicalConfig) && (
                    <MedicalConfigForm>
                        <MedicalConfigFormHeader>
                            {isCreatingNewConfig ? 'Create New Configuration' : `Edit: ${selectedMedicalConfig?.clientName}`}
                        </MedicalConfigFormHeader>

                        <InputGroup>
                            <Label>Client Name *</Label>
                            <Input
                                type="text"
                                placeholder="Enter client name"
                                value={medicalConfigForm.clientName}
                                onChange={(e) => setMedicalConfigForm({ ...medicalConfigForm, clientName: e.target.value })}
                                disabled={medicalConfigContainer.isSaving}
                            />
                        </InputGroup>

                        <InputGroup>
                            <PracticeManagementDropdown
                                label="Practice Management System *"
                                fieldName="integrationType"
                                placeholder="Select Practice Management System"
                                options={PRACTICE_MANAGEMENT_SYSTEMS.map(pms => ({
                                    label: pms,
                                    value: pms === 'Dentrix Ascend' ? 'DentrixAscend' : pms === 'Cloud 9' ? 'Cloud9' : pms
                                }))}
                                value={isCustomPMS ? 'Custom' : medicalConfigForm.integrationType}
                                handleOnChange={(fieldName, value) => {
                                    if (value === 'Custom') {
                                        setIsCustomPMS(true);
                                        // Clear the integrationType so user can type fresh
                                        setMedicalConfigForm({ ...medicalConfigForm, integrationType: '' });
                                    } else {
                                        setIsCustomPMS(false);
                                        setMedicalConfigForm({ ...medicalConfigForm, integrationType: value as string });
                                    }
                                }}
                                disabled={medicalConfigContainer.isSaving}
                            />
                            {isCustomPMS && (
                                <Input
                                    type="text"
                                    placeholder="Enter custom practice management system"
                                    value={medicalConfigForm.integrationType}
                                    onChange={(e) => setMedicalConfigForm({ ...medicalConfigForm, integrationType: e.target.value })}
                                    disabled={medicalConfigContainer.isSaving}
                                    style={{ marginTop: '8px' }}
                                />
                            )}
                        </InputGroup>

                        <InputGroup>
                            <LabelRow>
                                <Label>Configuration (JSON) *</Label>
                                <Button
                                    themes={['secondary-small']}
                                    text="Format JSON"
                                    onClick={formatJson}
                                    disabled={medicalConfigContainer.isSaving}
                                />
                            </LabelRow>
                            <ConfigTextarea
                                placeholder='{"key": "value"}'
                                value={medicalConfigForm.configuration}
                                onChange={(e) => setMedicalConfigForm({ ...medicalConfigForm, configuration: e.target.value })}
                                disabled={medicalConfigContainer.isSaving}
                                rows={10}
                            />
                        </InputGroup>

                        <MedicalConfigFormActions>
                            <Button
                                themes={['secondary']}
                                text="Cancel"
                                onClick={cancelMedicalConfigEdit}
                                disabled={medicalConfigContainer.isSaving}
                            />
                            <Button
                                themes={['primary']}
                                text={medicalConfigContainer.isSaving ? 'Saving...' : (isCreatingNewConfig ? 'Create Configuration' : 'Update Configuration')}
                                onClick={saveMedicalConfig}
                                disabled={medicalConfigContainer.isSaving}
                            />
                        </MedicalConfigFormActions>
                    </MedicalConfigForm>
                )}
            </MedicalConfigSection>

            <Divider />

            <Header>
                <Title>Healthcare Location Management</Title>
                <Description>
                    Manage healthcare locations and sync templates for dental/medical applications
                </Description>
            </Header>

            {healthcareApps.length === 0 ? (
                <EmptyState>
                    <EmptyStateText>No healthcare applications found in this organization.</EmptyStateText>
                </EmptyState>
            ) : (
                <>
                    <SearchBar>
                        <SearchInput
                            type="text"
                            placeholder='Search by name (e.g., location1|location2|!"exclude")'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <SearchInfo>
                            {searchTerm ? (
                                `Showing ${filteredApps.length} of ${healthcareApps.length} applications`
                            ) : (
                                'Use | to separate search terms (AND). Use !"text" to exclude apps containing text.'
                            )}
                        </SearchInfo>
                    </SearchBar>

                    <ActionsBar>
                        <SelectionControls>
                            <SelectionInfo>
                                <Checkbox
                                    type="checkbox"
                                    checked={selectedApps.size === healthcareApps.length}
                                    onChange={toggleSelectAll}
                                />
                                <span>{selectedApps.size} of {healthcareApps.length} selected</span>
                            </SelectionInfo>
                            <Button
                                themes={['secondary-small']}
                                text={`Select Visible (${filteredApps.length})`}
                                onClick={selectVisibleApps}
                                disabled={filteredApps.length === 0}
                            />
                        </SelectionControls>
                        <ButtonGroup>
                            {canManageQueues && <>
                                <Button
                                    themes={['secondary']}
                                    text={`Module Sync Selected (${selectedApps.size})`}
                                    onClick={moduleSyncSelectedLocations}
                                    disabled={selectedApps.size === 0 || isPublishing || !sharedTopicsApp}
                                />
                                <Button
                                    themes={['primary']}
                                    text={`Backup and Publish (${selectedApps.size})`}
                                    onClick={publishTemplatesForSelectedLocations}
                                    disabled={selectedApps.size === 0 || isPublishing || !applicationEnvironmentsContainer}
                                />
                            </>}
                        </ButtonGroup>
                    </ActionsBar>

                    {bulkOperationLogs.length > 0 && (
                        <SyncLogSection>
                            <SyncLogHeader>
                                <span>Sync Progress</span>
                                {!isPublishing && (
                                    <Button
                                        themes={['secondary-small']}
                                        text="Clear Log"
                                        onClick={clearLogs}
                                    />
                                )}
                            </SyncLogHeader>
                            <SyncLogArea>
                                {bulkOperationLogs.map((log, index) => (
                                    <SyncLogLine key={index}>{log}</SyncLogLine>
                                ))}
                            </SyncLogArea>
                        </SyncLogSection>
                    )}

                    <ApplicationsList>
                        {filteredApps.map(app => (
                            <ApplicationCard key={app.id} selected={selectedApps.has(app.id)}>
                                <CardContent onClick={() => toggleAppSelection(app.id)}>
                                    <Checkbox
                                        type="checkbox"
                                        checked={selectedApps.has(app.id)}
                                        onChange={() => toggleAppSelection(app.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <ApplicationInfo>
                                        <ApplicationName>{app.name}</ApplicationName>
                                    </ApplicationInfo>
                                </CardContent>
                            </ApplicationCard>
                        ))}
                    </ApplicationsList>
                </>
            )}

            {appliedTemplateContainer && (
                <>
                    <Header style={{ marginTop: '48px' }}>
                        <Title>Location-Specific Topics Export</Title>
                        <Description>
                            Select locations and topics to export as an Excel spreadsheet
                        </Description>
                    </Header>

                    {isLoadingSharedTopics ? (
                        <InlineLoaderWrapper>
                            <Loader />
                        </InlineLoaderWrapper>
                    ) : !sharedTopicsApp ? (
                        <EmptyState>
                            <EmptyStateText>No Shared Topics application found in this organization.</EmptyStateText>
                        </EmptyState>
                    ) : locationSpecificFields.length === 0 ? (
                        <EmptyState>
                            <EmptyStateText>No location-specific topics configured yet.</EmptyStateText>
                        </EmptyState>
                    ) : (
                        <>
                            <SelectionSection>
                                <SectionHeader>
                                    <SectionTitle>Select Topics</SectionTitle>
                                    <SelectionInfo>
                                        <Checkbox
                                            type="checkbox"
                                            checked={selectedFields.size === locationSpecificFields.length && locationSpecificFields.length > 0}
                                            onChange={toggleAllFields}
                                        />
                                        <span>{selectedFields.size} of {locationSpecificFields.length} selected</span>
                                    </SelectionInfo>
                                </SectionHeader>

                                <FieldsList>
                                    {locationSpecificFields.map((field, index) => (
                                        <FieldCard
                                            key={`${field.id}-${index}`}
                                            selected={selectedFields.has(field.title)}
                                            onClick={() => toggleFieldSelection(field.title)}
                                        >
                                            <Checkbox
                                                type="checkbox"
                                                checked={selectedFields.has(field.title)}
                                                onChange={() => toggleFieldSelection(field.title)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <FieldInfo>
                                                <FieldTitle>{field.title}</FieldTitle>
                                                {field.name && <FieldSection>Section: {field.name}</FieldSection>}
                                                {field.tip && <FieldDescription>{field.tip}</FieldDescription>}
                                            </FieldInfo>
                                        </FieldCard>
                                    ))}
                                </FieldsList>
                            </SelectionSection>

                            <Header style={{ marginTop: '32px' }}>
                                <Title>Import & Export</Title>
                                <Description>
                                    Import an Excel file to bulk update location-specific topics. The Excel file should have "Location ID" and "Location Name" columns, followed by the topic field columns.
                                </Description>
                            </Header>

                            <ImportExportButtonWrapper>
                                <ImportButtonWrapper>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={importFromExcel}
                                        style={{ display: 'none' }}
                                        id="excel-import-input-healthcare"
                                        disabled={isImporting || isApplying}
                                    />
                                    <label htmlFor="excel-import-input-healthcare">
                                        <Button
                                            themes={['secondary']}
                                            text={isImporting ? 'Importing...' : 'Import from Excel'}
                                            onClick={() => document.getElementById('excel-import-input-healthcare')?.click()}
                                            disabled={isImporting || isApplying}
                                        />
                                    </label>
                                    {importProgress && <ProgressText>{importProgress}</ProgressText>}
                                </ImportButtonWrapper>
                                <Button
                                    themes={['primary']}
                                    text={`Export to Excel (${selectedApps.size} locations, ${selectedFields.size} topics)`}
                                    onClick={exportToExcel}
                                    disabled={selectedApps.size === 0 || selectedFields.size === 0 || isImporting || isApplying}
                                />
                            </ImportExportButtonWrapper>

                            {importedData && importedData.length > 0 && (
                                <>
                                    <Header style={{ marginTop: '32px' }}>
                                        <Title>Review Imported Changes</Title>
                                        <Description>
                                            Review the imported data below. Click "Apply Changes" to save these updates.
                                        </Description>
                                    </Header>

                                    <ImportPreviewSection>
                                        <ImportPreviewHeader>
                                            <span>{importedData.length} locations with pending changes</span>
                                            <Button
                                                themes={['primary']}
                                                text={isApplying ? 'Applying...' : `Apply Changes to ${importedData.length} Locations`}
                                                onClick={applyChanges}
                                                disabled={isApplying || isImporting}
                                            />
                                        </ImportPreviewHeader>

                                        <ImportPreviewList>
                                            {importedData.slice(0, 10).map((location, index) => (
                                                <ImportPreviewCard key={index}>
                                                    <LocationHeader>
                                                        <LocationName>{location.locationName}</LocationName>
                                                        <LocationId>ID: {location.locationId}</LocationId>
                                                    </LocationHeader>
                                                    <FieldChangesList>
                                                        {location.fieldChanges.map((change, changeIndex) => (
                                                            <FieldChangeItem key={changeIndex}>
                                                                <FieldChangeLabel>{change.fieldTitle}:</FieldChangeLabel>
                                                                <FieldChangeValue>{change.newValue}</FieldChangeValue>
                                                            </FieldChangeItem>
                                                        ))}
                                                    </FieldChangesList>
                                                </ImportPreviewCard>
                                            ))}
                                            {importedData.length > 10 && (
                                                <MoreLocationsNote>
                                                    ...and {importedData.length - 10} more locations
                                                </MoreLocationsNote>
                                            )}
                                        </ImportPreviewList>
                                    </ImportPreviewSection>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            {successMessage && (
                <PageError errors={[successMessage]} type="success" />
            )}
            <PageError errors={errors} />
        </Container>
    );
};

// Wrapper component that provides the MedicalIntegrationConfigContainer
const OrganizationHealthcareManagementSettings: React.FC<OrganizationHealthcareManagementSettingsProps> = (props) => {
    return (
        <MedicalIntegrationConfigContainer.Provider>
            <OrganizationHealthcareManagementSettingsInner {...props} />
        </MedicalIntegrationConfigContainer.Provider>
    );
};

const PracticeManagementDropdown = styled(StyledDropdown)`
    .Select-control {
        border-radius: 4px !important;
    }
    .Select-menu-outer {
        border-radius: 4px !important;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
`;

const Description = styled.p`
    margin: 0;
    color: #666;
    font-size: 14px;
`;

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const EmptyStateText = styled.p`
    color: #666;
    font-size: 14px;
    margin: 0;
`;

const ActionsBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;

    @media (min-width: 1200px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
`;

const SelectionControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const SelectionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
`;

const Checkbox = styled.input`
    width: 18px;
    height: 18px;
    cursor: pointer;
`;

const ApplicationsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
`;

const ApplicationCard = styled.div<{ selected: boolean }>`
    border: 2px solid ${props => props.selected ? color_highlights_blue : color_shades_dark};
    border-radius: 8px;
    background: ${color_defaults_white};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const CardContent = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
`;

const ApplicationInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const ApplicationName = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
`;

const SelectionSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
`;

const FieldsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
`;

const FieldCard = styled.div<{ selected?: boolean }>`
    padding: 16px;
    background: ${color_defaults_white};
    border: 2px solid ${props => props.selected ? color_highlights_blue : color_shades_dark};
    border-radius: 8px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: ${props => props.selected !== undefined ? 'pointer' : 'default'};
    transition: all 0.2s ease;

    ${props => props.selected !== undefined && `
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
    `}
`;

const FieldInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
`;

const FieldTitle = styled.h4`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const FieldSection = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${color_highlights_blue};
    font-weight: 500;
`;

const FieldDescription = styled.p`
    margin: 0;
    font-size: 12px;
    color: #666;
    line-height: 1.4;
`;

const ImportExportButtonWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
    gap: 16px;
`;

const ImportButtonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ProgressText = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${color_highlights_blue};
    font-weight: 500;
`;

const ImportPreviewSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
`;

const ImportPreviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: ${color_defaults_white};
    border: 2px solid ${color_highlights_blue};
    border-radius: 8px;
    font-weight: 600;
`;

const ImportPreviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ImportPreviewCard = styled.div`
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const LocationHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${color_shades_dark};
`;

const LocationName = styled.h4`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
`;

const LocationId = styled.span`
    font-size: 13px;
    color: #666;
`;

const FieldChangesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FieldChangeItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const FieldChangeLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${color_highlights_blue};
`;

const FieldChangeValue = styled.span`
    font-size: 14px;
    color: #333;
    padding: 8px;
    background: #f5f5f5;
    border-radius: 4px;
`;

const MoreLocationsNote = styled.p`
    margin: 0;
    padding: 16px;
    text-align: center;
    font-size: 14px;
    color: #666;
    font-style: italic;
    background: ${color_defaults_white};
    border: 1px dashed ${color_shades_dark};
    border-radius: 8px;
`;

const SearchBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const SearchInput = styled.input`
    padding: 12px 16px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }

    &::placeholder {
        color: #999;
    }
`;

const SearchInfo = styled.div`
    font-size: 13px;
    color: #666;
    font-style: italic;
`;

// Medical Integration Configuration styled components
const MedicalConfigSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const MedicalConfigActions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const MedicalConfigList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const MedicalConfigListHeader = styled.h4`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
`;

const MedicalConfigCard = styled.div<{ selected?: boolean }>`
    padding: 16px;
    background: ${props => props.selected ? '#f0f7ff' : color_defaults_white};
    border: 2px solid ${props => props.selected ? color_highlights_blue : color_shades_dark};
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${color_highlights_blue};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
`;

const MedicalConfigCardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const MedicalConfigName = styled.h5`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
`;

const MedicalConfigType = styled.span`
    font-size: 14px;
    color: #555;
    font-weight: 500;
`;

const MedicalConfigIdRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MedicalConfigId = styled.span`
    font-size: 12px;
    color: #666;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
`;

const CopyButton = styled.button`
    background: ${color_highlights_blue};
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 11px;
    cursor: pointer;
    font-weight: 500;
    transition: background 0.2s;

    &:hover {
        background: #0056b3;
    }

    &:active {
        background: #004494;
    }
`;

const MedicalConfigForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: #f9f9f9;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const MedicalConfigFormHeader = styled.h4`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
`;

const MedicalConfigFormActions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LabelRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const Input = styled.input`
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
    }

    &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
    }

    &::placeholder {
        color: #999;
    }
`;

const ConfigTextarea = styled.textarea`
    padding: 12px;
    font-size: 13px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;
    resize: vertical;
    min-height: 150px;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
    }

    &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
    }

    &::placeholder {
        color: #999;
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${color_shades_dark};
    margin: 24px 0;
`;

const InlineLoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
    position: relative;
    
    /* Override the absolute positioning of the Loader */
    svg {
        position: static;
        margin: 0;
    }
`;

const SyncLogSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 16px 0;
`;

const SyncLogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const SyncLogArea = styled.div`
    max-height: 300px;
    overflow-y: auto;
    padding: 16px;
    background: #1e1e1e;
    border-radius: 8px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    font-size: 12px;
`;

const SyncLogLine = styled.div`
    color: #d4d4d4;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;

    &:nth-of-type(odd) {
        color: #9cdcfe;
    }
`;

export default OrganizationHealthcareManagementSettings;
